import { useState, useEffect } from "react";
import {
  getServerTemplateConfig,
  type ServerTemplateConfig,
} from "@/lib/serverTemplateDetection";
import { isBaseTemplateFamily } from "@/lib/workerTemplateNames";
import { resolveArProvinceCode } from "@/lib/internationalHomeMuxArGeo";
import {
  hasActiveGeoSim,
  isGeoSimUrlActive,
  pinGeoSimParamsIfNeeded,
} from "@/lib/geoSim/session";
import {
  clientMapWithEdgeCta,
  isEdgeCtaConfig,
  isLiveEdgeConfig,
  resolveInitialTemplateConfig,
  resolveStickyPreviewAgainstEdge,
} from "@/lib/templateSelectionPriority";

/** Coherente con Index.tsx / SEO (US, España, resto → LATAM incl. UNKNOWN). */
export type WorkerSeoRegion = "US" | "SPAIN" | "LATAM";

function seoRegionFromCountry(
  country: string | undefined | null,
): WorkerSeoRegion {
  if (country === "US") return "US";
  if (country === "ES") return "SPAIN";
  return "LATAM";
}

function ctaFlagsEqual(
  a: ServerTemplateConfig,
  b: ServerTemplateConfig,
): boolean {
  return (
    !!a.showCommunity === !!b.showCommunity &&
    !!a.hideCta === !!b.hideCta &&
    !!a.showBirthday === !!b.showBirthday &&
    !!a.showWishlist === !!b.showWishlist &&
    !!a.showDualVideo === !!b.showDualVideo &&
    (a.ctaResolvedFrom || "") === (b.ctaResolvedFrom || "") &&
    (a.ctaResolvedMatched || "") === (b.ctaResolvedMatched || "")
  );
}

function clearAdminPreviewStorage(): void {
  try {
    sessionStorage.removeItem("admin_preview_country");
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem("test_country");
  } catch {
    /* ignore */
  }
}

/**
 * Template selection for all visitors.
 * Live Worker edge always wins; sticky admin storage never demotes real geo
 * unless the URL has explicit ?preview= / ?country= / geo_sim.
 */
export const useTemplateSelection = () => {
  const [config, setConfig] = useState<ServerTemplateConfig>(() => {
    const serverConfigEarly = getServerTemplateConfig();
    const geoSimActive =
      isGeoSimUrlActive() ||
      (typeof window !== "undefined" && hasActiveGeoSim());

    let sticky: string | null = null;
    let test: string | null = null;
    let urlCountry: string | null = null;
    let urlPreview: string | null = null;
    if (typeof window !== "undefined") {
      try {
        sticky = sessionStorage.getItem("admin_preview_country");
      } catch {
        /* ignore */
      }
      try {
        test = localStorage.getItem("test_country");
      } catch {
        /* ignore */
      }
      try {
        const params = new URLSearchParams(window.location.search);
        const c = params.get("country");
        if (c && c.length === 2) urlCountry = c.toUpperCase();
        const p = params.get("preview");
        if (p === "international") urlPreview = "US";
        if (p === "argentina") urlPreview = "AR";
      } catch {
        /* ignore */
      }
    }

    // Only pass sticky into resolver when URL is explicit — otherwise ignored.
    const urlExplicit = !!(urlCountry || urlPreview);
    return resolveInitialTemplateConfig(serverConfigEarly, {
      geoSimActive,
      urlCountryOverride: urlCountry,
      urlPreviewCountry: urlPreview,
      stickyPreviewCountry: urlExplicit ? sticky : null,
      testCountry: urlExplicit ? test : null,
    });
  });

  // Re-read __TEMPLATE_CONFIG__ after first paint (script order / hydration).
  useEffect(() => {
    const fresh = getServerTemplateConfig();
    if (!fresh) return;
    setConfig((prev) => {
      if (
        prev.country === fresh.country &&
        prev.templateName === fresh.templateName &&
        prev.language === fresh.language &&
        prev.detectionMethod === fresh.detectionMethod &&
        prev.province === fresh.province &&
        (prev.cfCountry || "") === (fresh.cfCountry || "") &&
        ctaFlagsEqual(prev, fresh)
      ) {
        return prev;
      }
      // Prefer live edge over any client-only map
      if (isLiveEdgeConfig(fresh)) return fresh;
      if (isLiveEdgeConfig(prev) && !isLiveEdgeConfig(fresh)) return prev;
      if (isEdgeCtaConfig(fresh)) return fresh;
      return fresh;
    });
  }, []);

  // Admin preview URL only. Sticky without URL is cleaned up, never applied.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const previewMode = params.get("preview");

    if (hasActiveGeoSim() || params.get("geo_sim") === "1") {
      pinGeoSimParamsIfNeeded();
      const fresh = getServerTemplateConfig();
      if (fresh) setConfig(fresh);
      return;
    }

    if (previewMode === "exit") {
      clearAdminPreviewStorage();
      params.delete("preview");
      const newUrl = `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}`;
      window.history.replaceState({}, "", newUrl);
      window.location.reload();
      return;
    }

    if (previewMode === "international" || previewMode === "argentina") {
      const previewCountry = previewMode === "argentina" ? "AR" : "US";
      try {
        sessionStorage.setItem("admin_preview_country", previewCountry);
        localStorage.setItem("test_country", previewCountry);
      } catch {
        /* ignore */
      }
      const edge = getServerTemplateConfig();
      setConfig(clientMapWithEdgeCta(previewCountry, edge));
      return;
    }

    // Explicit ?country=XX (shareable preview / e2e)
    const countryOverride = params.get("country");
    if (countryOverride && countryOverride.length === 2) {
      const code = countryOverride.toUpperCase().slice(0, 2);
      try {
        localStorage.setItem("test_country", code);
        sessionStorage.setItem("admin_preview_country", code);
      } catch {
        /* ignore */
      }
      const edge = getServerTemplateConfig();
      setConfig(clientMapWithEdgeCta(code, edge));
      return;
    }

    // No URL intent: clear leftover sticky and force live edge for everyone.
    let storedPreview: string | null = null;
    try {
      storedPreview = sessionStorage.getItem("admin_preview_country");
    } catch {
      /* ignore */
    }
    const edge = getServerTemplateConfig();
    if (storedPreview && /^[A-Z]{2}$/.test(storedPreview)) {
      const resolved = resolveStickyPreviewAgainstEdge(storedPreview, edge, {
        urlExplicit: false,
      });
      if (resolved.clearSticky) {
        clearAdminPreviewStorage();
      }
      if (resolved.config) {
        setConfig(resolved.config);
        return;
      }
    }

    // Ensure config tracks live edge even if first paint was fallback
    if (edge && isLiveEdgeConfig(edge)) {
      setConfig((prev) => {
        if (
          prev.country === edge.country &&
          prev.templateName === edge.templateName &&
          ctaFlagsEqual(prev, edge) &&
          isLiveEdgeConfig(prev)
        ) {
          return prev;
        }
        return edge;
      });
    }
  }, []);

  const shouldUseBaseTemplate = () => isBaseTemplateFamily(config.templateName);
  const shouldUseBioLinkTemplate = () =>
    config.templateName === "BioLinkTemplate";
  const shouldUseArgentinaBioTemplate = () =>
    config.templateName === "ArgentinaBioTemplate";
  const shouldUseBioTemplatePremium = () =>
    config.templateName === "BioTemplatePremium";

  const region = seoRegionFromCountry(config.country);

  return {
    templateName: config.templateName,
    shouldUseBaseTemplate,
    shouldUseBioLinkTemplate,
    shouldUseArgentinaBioTemplate,
    shouldUseBioTemplatePremium,

    country: config.country,
    region,
    province: config.province,
    city: config.city || "",
    language: config.language,

    isSpainBio:
      config.templateName === "BioLinkTemplate" &&
      config.bioLayout === "spain_bio_link",
    isUSBio:
      config.templateName === "BioLinkTemplate" &&
      config.bioLayout === "us_bio_link",
    isBaseTemplate: isBaseTemplateFamily(config.templateName),
    isBaseTemplateBlack: config.templateName === "BaseTemplateBlack",
    isArgentinaBio: config.templateName === "ArgentinaBioTemplate",
    isPremiumInvite: config.templateName === "BioTemplatePremium",

    isArgentina: config.country === "AR",
    isBuenosAires: (() => {
      const code = resolveArProvinceCode(config.province);
      return code === "Buenos Aires" || code === "CABA";
    })(),

    whatsappBroadcast: !!config.whatsappBroadcast,
    showWishlist: !!config.showWishlist,
    vpnDetected: config.vpnDetected === true,
    asOrganization: config.asOrganization || null,
    isSpainBioCountry: config.bioLayout === "spain_bio_link",

    bioLayout: config.bioLayout,

    timezone:
      config.timezone ||
      (typeof window !== "undefined"
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : null),
    browserLang: typeof window !== "undefined" ? navigator.language : null,

    isEnhancedLoaded: true,
    detectionMethod: config.detectionMethod,
    isLoading: false,
    loadingStage: "complete" as const,
    loadingText: "",
    isReady: true,

    isProtected: false,
    isInCooldown: false,
    canChangeTemplate: true,
    requestTemplateChange: () => {},
    loadingProgress: 100,
  };
};
